import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import imageCompression from 'browser-image-compression'
import DefaultImagePNG from '../../../assets/images/DefaultProfilePic.png'
import DefaultWaterMarkPNG from '../../../assets/images/waterMarkIcon.png'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  Sparkles,
  User,
  Share2,
  Phone,
  Mail,
  Globe,
  Instagram,
  Facebook,
  Youtube,
  ImageIcon,
  ChevronRight,
  BadgeCheck,
  UserRound,
  Stamp,
  Building2,
  MapPin,
  Receipt,
  Plus,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react'
import AddBranchModal from '../../molecules/Dashboard/Branch/AddBranchModal'
import DeletePopup from '../../atoms/Dashboard/Folder/DeletePopup'
import { getBranchesAPI, deleteBranchAPI } from '../../../Redux/ApiCalls/Dashboard/BranchAPI'
import { BranchProfile } from '../../../Data/branch.dto'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import {
  getBusinessDetailsAPI,
  updateUserDetailsAPI,
  verifyEmailLinkAPI,
} from '../../../Redux/ApiCalls/Auth/user'
import { getUserByToken } from '../../../Redux/ApiCalls/Auth/login'
import LoadingDots from '../../atoms/Utlis/LoadinDots'
import { showErrorToast } from '../../atoms/Utlis/Toast'
import { IMAGE_CDN } from '../../../config/config'
import { resolveImage } from '../../../Utils/helper'
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ProgressWrap,
  ProgressLabel,
  ProgressPct,
  ProgressTrack,
  ProgressFill,
  ProgressHint,
  ScrollBody,
  FormGrid,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  PhotoUploadArea,
  PhotoRing,
  MemberImage,
  PhotoBadge,
  PhotoHint,
  PhotoSubhint,
  FieldGrid,
  Field,
  FieldFull,
  Label,
  FieldHint,
  Input,
  PhoneRow,
  CountryCodeBox,
  PhoneInput,
  ErrorSpan,
  SubmitBar,
  SubmitHint,
  SubmitButton,
  gradient,
  FormLabelIcon,
  FormGradientDefs,
} from '../../molecules/Dashboard/Member/memberFormStyles'

const ALLOWED_STUDIO_LOGO_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml',
  'image/bmp',
  'image/heic',
  'image/heif',
  'image/tiff',
]

const ALLOWED_STUDIO_LOGO_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.svg',
  '.bmp',
  '.heic',
  '.heif',
  '.tiff',
  '.tif',
]

function isValidStudioLogoFile(file: File): boolean {
  if (file.type && ALLOWED_STUDIO_LOGO_TYPES.includes(file.type.toLowerCase())) {
    return true
  }
  const name = file.name || ''
  const dotIdx = name.lastIndexOf('.')
  if (dotIdx !== -1) {
    const ext = name.substring(dotIdx).toLowerCase()
    return ALLOWED_STUDIO_LOGO_EXTENSIONS.includes(ext)
  }
  return false
}

function getProfileProgress(name: string, email: string, hasPhoto: boolean) {
  let done = 0
  if (name.trim().length > 0) done++
  if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email)) done++
  if (hasPhoto) done++
  return Math.round((done / 3) * 100)
}

export const GST_NUMBER_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][0-9A-Z][0-9A-Z]$/

const UserProfilePage: React.FC = () => {
  const { user, SocialLinks, isSocialChanged, loading, watermark, business, studio_logo, branches } =
    useAppSelector((state) => state.auth)
  const [showAddBranchModal, setShowAddBranchModal] = useState(false)
  const [editingBranch, setEditingBranch] = useState<BranchProfile | null>(null)
  const [deletingBranch, setDeletingBranch] = useState<BranchProfile | null>(null)
  const [isDeletingBranch, setIsDeletingBranch] = useState(false)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userContact, setUserContact] = useState('')
  const [userAddress, setUserAddress] = useState('')
  const [studioName, setStudioName] = useState('')
  const [gstNumber, setGstNumber] = useState('')
  const [imagePreview, setImagePreview] = useState('')
  const [userWebsite, setUserWebsite] = useState<string>()
  const [userInsta, setUserInsta] = useState<string>()
  const [userFacebook, setUserFacebook] = useState<string>()
  const [userYoutube, setUserYoutube] = useState<string>()
  const [dataChange, setDataChange] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [waterMarkPreviewImage, setWatermarkPreviewImage] = useState('')
  const [watermarkSelectedImage, setWatermarkSelectedImage] = useState<File>()
  const [studioLogoPreview, setStudioLogoPreview] = useState('')
  const [studioLogoSelected, setStudioLogoSelected] = useState<File>()
  const [showError, setShowError] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const watermarkInputRef = useRef<HTMLInputElement>(null)
  const studioLogoInputRef = useRef<HTMLInputElement>(null)

  const isRole4 = user?.role === 4

  useEffect(() => {
    dispatch(getUserByToken())
  }, [dispatch])

  useEffect(() => {
    if (user) {
      setUserName(user.name || '')
      setUserEmail(user.email || '')
      const rawPhone = String(user.phone_number || (user as any).phone || (user as any).mobile || '').trim()
      const cc = (user as any).country_code || '+91'
      let cleanPhone = rawPhone
      if (cc && cleanPhone.startsWith(cc)) {
        cleanPhone = cleanPhone.slice(cc.length).trim()
      } else if (cleanPhone.startsWith('+91')) {
        cleanPhone = cleanPhone.slice(3).trim()
      } else if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
        cleanPhone = cleanPhone.slice(2).trim()
      }
      setUserContact(cleanPhone)

      if (!selectedImage) {
        const rawImg = user.image || (user as any).profile_image
        if (rawImg) {
          setImagePreview(resolveImage(rawImg) || `${IMAGE_CDN}/${rawImg}`)
        } else {
          setImagePreview('')
        }
      }
      setSelectedImage(null)
    }
    if (business?.address) {
      setUserAddress(business.address)
    } else if (user?.address) {
      setUserAddress(user.address)
    }
    if (business?.studio_name) {
      setStudioName(business.studio_name)
    } else if (user?.studio_name) {
      setStudioName(user.studio_name)
    }
    if (business?.gst_number) {
      setGstNumber(business.gst_number)
    } else if ((user as any)?.gst_number) {
      setGstNumber((user as any).gst_number)
    }
    if (!isSocialChanged) {
      if (SocialLinks.website) setUserWebsite(SocialLinks.website)
      if (SocialLinks.facebook_link) setUserFacebook(SocialLinks.facebook_link)
      if (SocialLinks.insta_link) setUserInsta(SocialLinks.insta_link)
      if (SocialLinks.youtube_link) setUserYoutube(SocialLinks.youtube_link)
    }
    if (watermark) {
      setWatermarkPreviewImage(resolveImage(watermark) || `${IMAGE_CDN}/${watermark}`)
    } else if (business?.watermark) {
      setWatermarkPreviewImage(resolveImage(business.watermark) || `${IMAGE_CDN}/${business.watermark}`)
    }
    if (studio_logo) {
      setStudioLogoPreview(resolveImage(studio_logo) || `${IMAGE_CDN}/${studio_logo}`)
    } else if (business?.studio_logo) {
      setStudioLogoPreview(resolveImage(business.studio_logo) || `${IMAGE_CDN}/${business.studio_logo}`)
    }
  }, [user, SocialLinks, watermark, business, studio_logo, isSocialChanged])

  useEffect(() => {
    if (isSocialChanged && !isRole4) {
      dispatch(getBusinessDetailsAPI())
    }
  }, [isSocialChanged, dispatch, isRole4])

  useEffect(() => {
    if (isRole4) {
      setDataChange(selectedImage !== null)
      return
    }

    if (
      isDataChange() ||
      isInstaChange() ||
      isWebsiteChange() ||
      isFaceBookChange() ||
      isYoutubeChange() ||
      isAddressChange() ||
      isStudioNameChange() ||
      isGstChange() ||
      selectedImage !== null ||
      watermarkSelectedImage !== undefined ||
      studioLogoSelected !== undefined
    ) {
      setDataChange(true)
    } else {
      setDataChange(false)
    }
  }, [
    isRole4,
    userWebsite,
    userFacebook,
    userInsta,
    userYoutube,
    userAddress,
    studioName,
    gstNumber,
    userName,
    userEmail,
    selectedImage,
    watermarkSelectedImage,
    studioLogoSelected,
  ])

  const progress = useMemo(
    () => getProfileProgress(userName, userEmail, Boolean(imagePreview)),
    [userName, userEmail, imagePreview]
  )

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type })

  const handleDeleteBranch = async () => {
    if (!deletingBranch) return
    setIsDeletingBranch(true)
    try {
      const resultAction = await dispatch(deleteBranchAPI(deletingBranch.id))
      if (deleteBranchAPI.fulfilled.match(resultAction)) {
        setDeletingBranch(null)
        dispatch(getUserByToken())
      } else {
        const errorMsg =
          typeof resultAction.payload === 'string'
            ? resultAction.payload
            : (resultAction.payload as any)?.message || 'Failed to delete branch'
        showErrorToast(errorMsg)
      }
    } catch (err: any) {
      showErrorToast(err?.message || 'Failed to delete branch')
    } finally {
      setIsDeletingBranch(false)
    }
  }

  const handleDivClick = () => fileInputRef.current?.click()

  const compressImage = async (file: File): Promise<Blob> => {
    try {
      return imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: false,
      })
    } catch (error) {
      console.error('Error compressing image:', error)
      throw error
    }
  }

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return
    let file = event.target.files[0]
    if (file.size / 1024 / 1024 > 2) {
      file = blobToFile(await compressImage(file), file.name)
    }
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      setSelectedImage(file)
      setImagePreview(reader.result as string)
    }
  }

  const handleStudioLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0]) return
    let file = event.target.files[0]

    if (!isValidStudioLogoFile(file)) {
      showErrorToast(
        'Invalid studio logo format. Allowed formats: JPG, JPEG, PNG, WEBP, GIF, SVG, BMP, HEIC, TIFF.'
      )
      if (event.target) event.target.value = ''
      return
    }

    const isCompressible = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ].includes(file.type.toLowerCase())
    if (isCompressible && file.size / 1024 / 1024 > 2) {
      try {
        file = blobToFile(await compressImage(file), file.name)
      } catch (err) {
        console.error('Image compression failed:', err)
      }
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setStudioLogoSelected(file)
      setStudioLogoPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleWatermArkImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0]) return
    let file = event.target.files[0]
    const image = new Image()
    const reader = new FileReader()

    reader.onloadend = () => {
      image.src = reader.result as string
      image.onload = async () => {
        const { width, height } = image
        if (width < 160 || height < 160) {
          showErrorToast('Image dimensions should not exceed 160px X 160px.')
          return
        }
        if (file.size / 1024 / 1024 > 2) {
          file = blobToFile(await compressImage(file), file.name)
        }
        setWatermarkSelectedImage(file)
        setWatermarkPreviewImage(reader.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  const onChangeData = (name: string, value: string) => {
    if (name === 'name') {
      if (value.length < 25) setUserName(value)
    } else if (name === 'email') {
      if (value.length < 45) setUserEmail(value)
    } else if (name === 'website') {
      setUserWebsite(value)
    } else if (name === 'insta') {
      setUserInsta(value)
    } else if (name === 'facebook') {
      setUserFacebook(value)
    } else if (name === 'youtube') {
      setUserYoutube(value)
    } else if (name === 'address') {
      setUserAddress(value)
    } else if (name === 'studio_name') {
      setStudioName(value)
    } else if (name === 'gst_number') {
      if (value.length <= 15) setGstNumber(value.toUpperCase())
    }
  }

  function isValidURL(url: string) {
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' +
      "((([a-zA-Z0-9$_.+!*',;?&=-]|%[0-9a-fA-F]{2})+:)*([a-zA-Z0-9$_.+!*',;?&=-]|%[0-9a-fA-F]{2})+@)?" +
      '((\\[(|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))\\])|' +
      "(([a-zA-Z0-9$_.+!*',;?&=-]|%[0-9a-fA-F]{2})+\\.)*[a-zA-Z0-9](([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)+" +
      '[a-zA-Z0-9-]{2,63}\\.?)' +
      '(:\\d{1,5})?' +
      "(\\/([a-zA-Z0-9$_.+!*',;:@&=-]|%[0-9a-fA-F]{2})*)*" +
      "(\\?[a-zA-Z0-9$_.+!*',;:@&=-]*(\\([a-zA-Z0-9$_.+!*',;:@&=-]|%[0-9a-fA-F]{2})*\\))*" +
      "(#([a-zA-Z0-9$_.+!*',;:@&=-]|%[0-9a-fA-F]{2})*)?$",
      'i'
    )
    return pattern.test(url)
  }

  const isDataChange = () =>
    userName !== user?.name ||
    userEmail !== user?.email ||
    Boolean(watermarkSelectedImage) ||
    Boolean(studioLogoSelected)

  const isAddressChange = () => {
    const currentAddress = business?.address || user?.address || ''
    return userAddress !== currentAddress
  }

  const isStudioNameChange = () => {
    const currentStudioName = business?.studio_name || user?.studio_name || ''
    return studioName !== currentStudioName
  }

  const isGstChange = () => {
    const currentGst = business?.gst_number || (user as any)?.gst_number || ''
    return gstNumber.trim() !== currentGst.trim()
  }

  const isValidEmail = (value: string) =>
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value)

  const isValidData = (name: string) => {
    if (name === 'name') return userName.length > 0
    if (name === 'email') return userEmail.length > 0 && isValidEmail(userEmail)
    if (name === 'website') {
      if (userWebsite) return userWebsite.length > 0 ? isValidURL(userWebsite) : false
      return true
    }
    if (name === 'insta' && userInsta) {
      return userInsta.length > 0 ? isValidURL(userInsta) : false
    }
    if (name === 'facebook' && userFacebook) {
      return userFacebook.length > 0 ? isValidURL(userFacebook) : false
    }
    if (name === 'youtube' && userYoutube) {
      return userYoutube.length > 0 ? isValidURL(userYoutube) : false
    }
    if (name === 'address') return true
    if (name === 'studio_name') return true
    if (name === 'gst_number') {
      if (!gstNumber || !gstNumber.trim()) return true
      return GST_NUMBER_REGEX.test(gstNumber.trim().toUpperCase())
    }
    return true
  }

  const isWebsiteChange = () => {
    if (SocialLinks.website) return SocialLinks.website !== userWebsite
    return Boolean(userWebsite && userWebsite.length > 0)
  }

  const isInstaChange = () => {
    if (SocialLinks.insta_link) return SocialLinks.insta_link !== userInsta
    return Boolean(userInsta && userInsta.length > 0)
  }

  const isFaceBookChange = () => {
    if (SocialLinks.facebook_link) return SocialLinks.facebook_link !== userFacebook
    return Boolean(userFacebook && userFacebook.length > 0)
  }

  const isYoutubeChange = () => {
    if (SocialLinks.youtube_link) return SocialLinks.youtube_link !== userYoutube
    return Boolean(userYoutube && userYoutube.length > 0)
  }

  function handleSubmit(): void {
    if (isRole4) {
      if (selectedImage) {
        const formData = new FormData()
        formData.append('name', userName || user?.name || '')
        formData.append('image', selectedImage)
        dispatch(updateUserDetailsAPI(formData))
      }
      return
    }

    if (
      isValidData('name') &&
      isValidData('youtube') &&
      isValidData('email') &&
      isValidData('insta') &&
      isValidData('facebook') &&
      isValidData('website') &&
      isValidData('address') &&
      isValidData('studio_name') &&
      isValidData('gst_number')
    ) {
      const formData = new FormData()
      formData.append('name', userName)
      if (userEmail !== user?.email) formData.append('email', userEmail)
      if (userWebsite) formData.append('website', userWebsite)
      if (userInsta) formData.append('insta_link', userInsta)
      if (userFacebook) formData.append('facebook_link', userFacebook)
      if (userYoutube) formData.append('youtube_link', userYoutube)
      if (userAddress !== undefined) formData.append('address', userAddress)
      if (studioName !== undefined) formData.append('studio_name', studioName)
      if (gstNumber !== undefined) formData.append('gst_number', gstNumber.trim().toUpperCase())
      if (selectedImage) formData.append('image', selectedImage)
      if (watermarkSelectedImage) formData.append('watermark', watermarkSelectedImage)
      if (studioLogoSelected) formData.append('studio_logo', studioLogoSelected)
      dispatch(updateUserDetailsAPI(formData))
    } else {
      setShowError(true)
      if (!isValidData('gst_number')) {
        showErrorToast('Please enter a valid 15-character GST number (e.g. 24ABCDE1234F1Z5).')
      }
    }
  }

  const handleVerifyEmail = (email: string) => {
    dispatch(verifyEmailLinkAPI({ email }))
  }

  if (!user) return null

  return (
    <PageContainer>
      <FormGradientDefs />
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={10} />
                  {isRole4 ? 'Your profile' : 'Your studio profile'}
                </HeroBadge>
                <PageTitle className="linear-text">Profile settings</PageTitle>
                <PageSubtitle>
                  {isRole4
                    ? 'View your personal profile details and update your profile photo.'
                    : 'Manage your account details, studio logo, social links, and album watermark for your studio brand.'}
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
          <HeroRightActions>
            {user?.role === 3 && (
              <ViewCustomersHeroBtn
                type="button"
                onClick={() => navigate('/dashboard/customers/all')}
              >
                <Users size={16} />
                <span>View Customers</span>
              </ViewCustomersHeroBtn>
            )}
            {!isRole4 && (
              <ProgressWrap>
                <ProgressLabel>
                  Profile completion
                  <ProgressPct>{progress}%</ProgressPct>
                </ProgressLabel>
                <ProgressTrack>
                  <ProgressFill $pct={progress} />
                </ProgressTrack>
                <ProgressHint>
                  {progress === 100 ? 'Profile looks complete' : 'Add photo & details to finish setup'}
                </ProgressHint>
              </ProgressWrap>
            )}
          </HeroRightActions>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <FormGrid
          style={
            isRole4
              ? {
                  gridTemplateColumns: '1fr',
                  maxWidth: 580,
                  margin: '0 auto',
                  width: '100%',
                }
              : undefined
          }
        >
          <FormCard>
            <CardHeader>
              <CardIcon>
                <User size={20} />
              </CardIcon>
              <div>
                <CardTitle>Personal details</CardTitle>
                <CardSubtitle>
                  {isRole4
                    ? 'Profile photo, name, and phone linked to your account'
                    : 'Name, email, and phone linked to your account'}
                </CardSubtitle>
              </div>
            </CardHeader>

            <PhotoUploadArea onClick={handleDivClick}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              <PhotoRing>
                <MemberImage
                  src={imagePreview || DefaultImagePNG}
                  alt={userName || 'Profile'}
                />
                <PhotoBadge>
                  <Camera size={16} />
                </PhotoBadge>
              </PhotoRing>
              <PhotoHint>{imagePreview ? 'Tap to change photo' : 'Upload profile photo'}</PhotoHint>
              <PhotoSubhint>JPG or PNG · Max 2 MB</PhotoSubhint>
            </PhotoUploadArea>

            <Field>
              <Label htmlFor="profile-name">
                <FormLabelIcon icon={UserRound} />
                Full name
              </Label>
              <Input
                id="profile-name"
                value={userName}
                readOnly={isRole4}
                disabled={isRole4}
                onChange={(e) => !isRole4 && onChangeData('name', e.target.value)}
                placeholder="Your display name"
                style={
                  isRole4
                    ? {
                        cursor: 'not-allowed',
                        background: 'rgba(23, 23, 23, 0.04)',
                        color: 'rgba(23, 23, 23, 0.7)',
                        borderColor: 'rgba(23, 23, 23, 0.08)',
                      }
                    : undefined
                }
              />
              {isRole4 ? (
                <FieldHint>Display name assigned by studio administrator</FieldHint>
              ) : (
                showError && !isValidData('name') && (
                  <ErrorSpan>Please provide a valid name.</ErrorSpan>
                )
              )}
            </Field>

            {!isRole4 && (
              <Field>
                <LabelRow>
                  <Label htmlFor="profile-email">
                    <FormLabelIcon icon={Mail} />
                    Email address
                  </Label>
                  <VerifyPill
                    type="button"
                    $verified={Boolean(user.is_email_verified)}
                    disabled={Boolean(user.is_email_verified)}
                    onClick={() => {
                      if (!user.is_email_verified) handleVerifyEmail(user.email)
                    }}
                  >
                    {user.is_email_verified ? (
                      <>
                        <BadgeCheck size={13} />
                        Verified
                      </>
                    ) : (
                      'Verify email'
                    )}
                  </VerifyPill>
                </LabelRow>
                <Input
                  id="profile-email"
                  value={userEmail}
                  onChange={(e) => onChangeData('email', e.target.value)}
                  placeholder="you@studio.com"
                  type="email"
                />
                {showError && !isValidData('email') && (
                  <ErrorSpan>Please provide a valid email address.</ErrorSpan>
                )}
              </Field>
            )}

            <Field>
              <LabelRow>
                <Label htmlFor="profile-phone">
                  <FormLabelIcon icon={Phone} />
                  Phone number
                </Label>
                {!isRole4 && (
                  <ChangeLink
                    type="button"
                    onClick={() => navigate('/dashboard/user/changePhoneNumber')}
                  >
                    Change
                    <ChevronRight size={14} />
                  </ChangeLink>
                )}
              </LabelRow>
              <PhoneRow
                as="div"
                onClick={
                  isRole4
                    ? undefined
                    : () => navigate('/dashboard/user/changePhoneNumber')
                }
                role={isRole4 ? undefined : 'button'}
                tabIndex={isRole4 ? -1 : 0}
                onKeyDown={(e) => {
                  if (!isRole4 && (e.key === 'Enter' || e.key === ' ')) {
                    navigate('/dashboard/user/changePhoneNumber')
                  }
                }}
                style={{
                  cursor: isRole4 ? 'default' : 'pointer',
                  background: isRole4 ? 'rgba(23, 23, 23, 0.04)' : undefined,
                  borderColor: isRole4 ? 'rgba(23, 23, 23, 0.08)' : undefined,
                }}
              >
                <CountryCodeBox>
                  <span>{(user as any)?.country_code || '+91'}</span>
                </CountryCodeBox>
                <PhoneInput
                  id="profile-phone"
                  value={userContact}
                  readOnly
                  disabled={isRole4}
                  placeholder="10-digit mobile number"
                  style={
                    isRole4
                      ? { cursor: 'default', color: 'rgba(23, 23, 23, 0.7)' }
                      : undefined
                  }
                />
              </PhoneRow>
              {isRole4 ? (
                <FieldHint>Registered mobile number linked to your account</FieldHint>
              ) : (
                <FieldHint>Tap to update your registered mobile number</FieldHint>
              )}
            </Field>

            {!isRole4 && (
              <>
                <Field>
                  <Label htmlFor="profile-studio-name">
                    <FormLabelIcon icon={Building2} />
                    Studio name
                  </Label>
                  <Input
                    id="profile-studio-name"
                    value={studioName}
                    onChange={(e) => onChangeData('studio_name', e.target.value)}
                    placeholder="e.g. Studio"
                  />
                  <FieldHint>Brand name of your studio shown on invoices, quotations, and orders</FieldHint>
                </Field>

                <Field>
                  <Label htmlFor="profile-address">
                    <FormLabelIcon icon={MapPin} />
                    Studio address
                  </Label>
                  <Input
                    id="profile-address"
                    value={userAddress}
                    onChange={(e) => onChangeData('address', e.target.value)}
                    placeholder="Studio address (e.g. Surat Rd NE)"
                  />
                  <FieldHint>Studio location shown on invoices and quotations</FieldHint>
                </Field>

                <Field>
                  <Label htmlFor="profile-gst-number">
                    <FormLabelIcon icon={Receipt} />
                    GST number
                  </Label>
                  <Input
                    id="profile-gst-number"
                    value={gstNumber}
                    onChange={(e) => onChangeData('gst_number', e.target.value)}
                    placeholder="15-character GSTIN (e.g. 24ABCDE1234F1Z5)"
                    maxLength={15}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {showError && !isValidData('gst_number') && (
                    <ErrorSpan>Please provide a valid 15-character GST number (e.g. 24ABCDE1234F1Z5).</ErrorSpan>
                  )}
                  <FieldHint>Official GST identification number of your studio for invoices and taxation</FieldHint>
                </Field>
              </>
            )}
          </FormCard>

          {!isRole4 && (
            <FormCard>
              <CardHeader>
                <CardIcon $tone="#35b7c8">
                  <Share2 size={20} />
                </CardIcon>
                <div>
                  <CardTitle>Social & branding</CardTitle>
                  <CardSubtitle>Links shown on albums and your studio presence</CardSubtitle>
                </div>
              </CardHeader>

              <FieldGrid>
                <FieldFull>
                  <Label htmlFor="profile-website">
                    <FormLabelIcon icon={Globe} />
                    Website
                  </Label>
                  <Input
                    id="profile-website"
                    value={userWebsite ?? ''}
                    onChange={(e) => onChangeData('website', e.target.value)}
                    placeholder="https://yourstudio.com"
                  />
                  {showError && !isValidData('website') && (
                    <ErrorSpan>Please provide a valid website link.</ErrorSpan>
                  )}
                </FieldFull>

                <FieldFull>
                  <Label htmlFor="profile-insta">
                    <FormLabelIcon icon={Instagram} />
                    Instagram
                  </Label>
                  <Input
                    id="profile-insta"
                    value={userInsta ?? ''}
                    onChange={(e) => onChangeData('insta', e.target.value)}
                    placeholder="https://instagram.com/yourstudio"
                  />
                  {showError && !isValidData('insta') && (
                    <ErrorSpan>Please provide a valid Instagram link.</ErrorSpan>
                  )}
                </FieldFull>

                <FieldFull>
                  <Label htmlFor="profile-facebook">
                    <FormLabelIcon icon={Facebook} />
                    Facebook
                  </Label>
                  <Input
                    id="profile-facebook"
                    value={userFacebook ?? ''}
                    onChange={(e) => onChangeData('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourstudio"
                  />
                  {showError && !isValidData('facebook') && (
                    <ErrorSpan>Please provide a valid Facebook link.</ErrorSpan>
                  )}
                </FieldFull>

                <FieldFull>
                  <Label htmlFor="profile-youtube">
                    <FormLabelIcon icon={Youtube} />
                    YouTube
                  </Label>
                  <Input
                    id="profile-youtube"
                    value={userYoutube ?? ''}
                    onChange={(e) => onChangeData('youtube', e.target.value)}
                    placeholder="https://youtube.com/@yourstudio"
                  />
                  {showError && !isValidData('youtube') && (
                    <ErrorSpan>Please provide a valid YouTube link.</ErrorSpan>
                  )}
                </FieldFull>
              </FieldGrid>

              <Field>
                <Label>
                  <FormLabelIcon icon={Building2} />
                  Studio logo
                </Label>
                <FieldHint>Shown on your studio brand, invoices, and client galleries</FieldHint>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/bmp,.heic,.heif,.tiff,image/tiff"
                  onChange={handleStudioLogoChange}
                  style={{ display: 'none' }}
                  ref={studioLogoInputRef}
                />
                <WatermarkArea
                  type="button"
                  onClick={() => studioLogoInputRef.current?.click()}
                >
                  <WatermarkPreview>
                    {studioLogoPreview ? (
                      <WatermarkImg src={studioLogoPreview} alt="Studio logo preview" />
                    ) : (
                      <WatermarkPlaceholder src={DefaultImagePNG} alt="" />
                    )}
                  </WatermarkPreview>
                  <WatermarkCopy>
                    <WatermarkTitle>
                      {studioLogoPreview ? 'Update studio logo' : 'Upload studio logo'}
                    </WatermarkTitle>
                    <WatermarkSubhint>JPG, PNG, WEBP, SVG, GIF, BMP, HEIC · Max 2 MB</WatermarkSubhint>
                  </WatermarkCopy>
                  <ImageIcon size={18} color="#c94daa" />
                </WatermarkArea>
              </Field>

              <Field>
                <Label>
                  <FormLabelIcon icon={Stamp} />
                  Album watermark
                </Label>
                <FieldHint>Applied to shared album images · min 160×160px</FieldHint>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleWatermArkImageChange}
                  style={{ display: 'none' }}
                  ref={watermarkInputRef}
                />
                <WatermarkArea
                  type="button"
                  onClick={() => watermarkInputRef.current?.click()}
                >
                  <WatermarkPreview>
                    {waterMarkPreviewImage ? (
                      <WatermarkImg src={waterMarkPreviewImage} alt="Watermark preview" />
                    ) : (
                      <WatermarkPlaceholder src={DefaultWaterMarkPNG} alt="" />
                    )}
                  </WatermarkPreview>
                  <WatermarkCopy>
                    <WatermarkTitle>
                      {waterMarkPreviewImage ? 'Update watermark' : 'Add watermark'}
                    </WatermarkTitle>
                    <WatermarkSubhint>PNG recommended · Max 160×160px</WatermarkSubhint>
                  </WatermarkCopy>
                  <ImageIcon size={18} color="#c94daa" />
                </WatermarkArea>
              </Field>
            </FormCard>
          )}

          {!isRole4 && (
            <FormCard style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <CardHeader style={{ marginBottom: 0 }}>
                  <CardIcon>
                    <Building2 size={20} />
                  </CardIcon>
                  <div>
                    <CardTitle>Studio branches</CardTitle>
                    <CardSubtitle>Manage your studio branches, local offices, and branch GST numbers</CardSubtitle>
                  </div>
                </CardHeader>
                <button
                  type="button"
                  onClick={() => setShowAddBranchModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    borderRadius: 10,
                    border: 'none',
                    background: 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)',
                    color: '#fff',
                    fontFamily: 'Urbanist, sans-serif',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  <Plus size={15} strokeWidth={2.5} /> Add branch
                </button>
              </div>

              {(!branches || branches.length === 0) ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', background: '#fafafa', borderRadius: 14, border: '1px dashed rgba(23, 23, 23, 0.12)', marginTop: 16 }}>
                  <Building2 size={32} color="#c94daa" style={{ margin: '0 auto 8px', opacity: 0.6 }} />
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#171717' }}>No branches added yet</div>
                  <div style={{ fontSize: 12, color: 'rgba(23, 23, 23, 0.55)', marginTop: 4 }}>
                    Add different studio branches to manage team members, events, quotations, orders, and invoices per branch.
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginTop: 16 }}>
                  {branches.map((b) => (
                    <div
                      key={b.id}
                      style={{
                        padding: '14px 16px',
                        borderRadius: 14,
                        background: '#fafafa',
                        border: '1px solid rgba(23, 23, 23, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#171717' }}>{b.branch_name}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'rgba(201, 77, 170, 0.1)', color: '#c94daa' }}>
                              {b.branch_city}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={() => setEditingBranch(b)}
                              title="Edit branch"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: 7,
                                border: '1px solid rgba(23, 23, 23, 0.12)',
                                background: '#ffffff',
                                cursor: 'pointer',
                                color: '#171717',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#c94daa'
                                e.currentTarget.style.color = '#c94daa'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'rgba(23, 23, 23, 0.12)'
                                e.currentTarget.style.color = '#171717'
                              }}
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeletingBranch(b)}
                              title="Delete branch"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 28,
                                height: 28,
                                borderRadius: 7,
                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                background: '#ffffff',
                                cursor: 'pointer',
                                color: '#ef4444',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fef2f2'
                                e.currentTarget.style.borderColor = '#ef4444'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#ffffff'
                                e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.25)'
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(23, 23, 23, 0.65)', lineHeight: 1.4 }}>
                          <MapPin size={12} style={{ display: 'inline', marginRight: 4, verticalAlign: -1 }} />
                          {b.branch_address}, {b.branch_city}, {b.branch_state} - {b.branch_pincode}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'rgba(23, 23, 23, 0.55)', marginTop: 4 }}>
                          <span><Phone size={11} style={{ display: 'inline', marginRight: 3, verticalAlign: -1 }} /> {b.branch_phone}</span>
                          <span>GST: <strong style={{ color: '#171717' }}>{b.gst_number}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FormCard>
            )}
          </FormGrid>

          <SubmitBar>
            <SubmitHint>
              {isRole4
                ? dataChange
                  ? 'You have selected a new profile photo. Save to update your profile.'
                  : 'Select a new profile photo above to enable saving.'
                : dataChange
                  ? 'You have unsaved changes. Save to update your profile and social links.'
                  : 'Make changes above to enable saving your profile.'}
            </SubmitHint>
            {loading ? (
              <LoadingWrap>
                <LoadingDots />
              </LoadingWrap>
            ) : (
              <SubmitButton type="button" $ready={dataChange} onClick={handleSubmit}>
                Save changes
              </SubmitButton>
            )}
          </SubmitBar>
        </ScrollBody>

        {showAddBranchModal && (
          <AddBranchModal onClose={() => setShowAddBranchModal(false)} />
        )}
        {editingBranch && (
          <AddBranchModal
            branch={editingBranch}
            onClose={() => setEditingBranch(null)}
            onSuccess={() => setEditingBranch(null)}
          />
        )}
        {deletingBranch && (
          <DeletePopup
            text={`Are you sure you want to delete branch "${deletingBranch.branch_name}"? Records linked to this branch will have their branch unassigned.`}
            buttonText="Delete branch"
            cancel={() => setDeletingBranch(null)}
            Delete={handleDeleteBranch}
            isDeleting={isDeletingBranch}
          />
        )}
    </PageContainer>
  )
}

export default UserProfilePage

const HeroRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`

const ViewCustomersHeroBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 12px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.3);
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(201, 77, 170, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`

const VerifyPill = styled.button<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$verified ? 'rgba(49, 166, 106, 0.3)' : 'rgba(228, 72, 98, 0.3)')};
  background: ${(p) => (p.$verified ? 'rgba(49, 166, 106, 0.1)' : 'rgba(228, 72, 98, 0.08)')};
  color: ${(p) => (p.$verified ? '#31a66a' : '#e44862')};
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: ${(p) => (p.$verified ? 'default' : 'pointer')};
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 1;
  }
`

const ChangeLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: none;
  background: none;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #c94daa;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const WatermarkArea = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 2px dashed rgba(201, 77, 170, 0.28);
  background: linear-gradient(180deg, rgba(201, 77, 170, 0.04) 0%, rgba(255, 201, 81, 0.04) 100%);
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.5);
    background: linear-gradient(180deg, rgba(201, 77, 170, 0.07) 0%, rgba(255, 201, 81, 0.06) 100%);
  }
`

const WatermarkPreview = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`

const WatermarkImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const WatermarkPlaceholder = styled.img`
  width: 24px;
  height: 24px;
  opacity: 0.55;
  object-fit: contain;
`

const WatermarkCopy = styled.div`
  flex: 1;
  min-width: 0;
`

const WatermarkTitle = styled.p`
  margin: 0 0 2px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`

const WatermarkSubhint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`

const LoadingWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 48px;
  padding: 0 28px;
  border-radius: 12px;
  background: ${gradient};
`
